/**
 * NOLAS 2026, application multi evenement e.labriquerie.net.
 * src/i18n/fr.rattachement.ts
 *
 * Messages du chantier « recruteurs rattachés » (décisions de Mika du
 * 2026-09-30) : bandeau du recruteur (une ligne et « En savoir plus »),
 * explication côté compte principal, demandes de rattachement (côté
 * demandeur, côté compte principal, côté organisation), récapitulatif
 * « qui a fait quoi », ligne compacte de Ma fiche, et les deux droits
 * virtuels (/admin/recruiter-links, /admin/premium-grant).
 *
 * Préfixes : `rattach.` (espace exposant), `rattachAdmin.` (organisation),
 * `adminNav.right.` pour les deux droits. Textes courts : l application se
 * lit surtout au téléphone. Ni tiret cadratin, ni nom d événement en dur.
 *
 * Mikael - chef de projet - 2026-09-30
 */
export const messagesRattachementFr: Record<string, string> = {
  // ── Droits virtuels (/admin/access) ─────────────────────────────────
  'adminNav.right.recruiterLinks': 'Valider les rattachements de recruteurs',
  'adminNav.right.premiumGrant': 'Accorder le premium aux entreprises',

  // ── Bandeau du recruteur ────────────────────────────────────────────
  'rattach.bandeau.ligne': 'Recruteur de {entreprise}, compte principal : {titulaire}.',
  'rattach.bandeau.ligneSansNom': 'Recruteur de {entreprise}.',
  'rattach.bandeau.plus': 'En savoir plus',
  'rattach.bandeau.detail.fiche': 'Seul le compte principal modifie la fiche, les offres et le logo.',
  'rattach.bandeau.detail.remontee':
    'Vos scans, notes, étoiles et informations remontent sur le compte principal, regroupés avec ceux des autres recruteurs.',

  // ── Mes recruteurs, côté compte principal ───────────────────────────
  'rattach.mere.resume': 'Tout se regroupe sur votre compte.',
  'rattach.mere.detail':
    'Les scans, notes, étoiles et informations de chaque recruteur remontent ici, avec leur nom. Vous seul modifiez la fiche, les offres et le logo.',
  'rattach.creneaux.premium': 'Les créneaux d’entretien et la recherche de CV sont réservés aux entreprises premium.',
  'rattach.liste.rattache': 'A rejoint sur demande',
  'rattach.action.detacher': 'Détacher',
  'rattach.detacher.confirmer':
    'Détacher {nom} ? Sa propre fiche lui sera rendue et ses actions ne remonteront plus chez vous.',
  'rattach.toast.detache': 'Compte détaché : sa fiche lui est rendue.',

  // ── Demandes reçues ─────────────────────────────────────────────────
  'rattach.entrantes.titre': 'Demandes à rejoindre votre entreprise',
  'rattach.entrantes.qui': '{nom}, de {origine}',
  'rattach.entrantes.message': '« {message} »',
  'rattach.entrantes.aide':
    'En acceptant, sa fiche est masquée du public (conservée) et ses scans, notes et étoiles remontent chez vous.',
  'rattach.action.accepter': 'Accepter',
  'rattach.action.refuser': 'Refuser',
  'rattach.refus.motif': 'Motif du refus (facultatif)',
  'rattach.refus.confirmer': 'Confirmer le refus',
  'rattach.action.annuler': 'Annuler',
  'rattach.toast.accepte': '{nom} a rejoint votre entreprise.',
  'rattach.toast.refuse': 'Demande refusée.',

  // ── Ma demande ──────────────────────────────────────────────────────
  'rattach.demande.titre': 'Rejoindre une autre entreprise',
  'rattach.demande.intro':
    'Votre entreprise est déjà inscrite par un collègue ? Demandez à la rejoindre : son compte principal valide.',
  'rattach.demande.recherche': 'Entreprise',
  'rattach.demande.placeholder': 'Tapez les premières lettres',
  'rattach.demande.aucun': 'Aucune entreprise ne correspond.',
  'rattach.demande.choisie': 'Entreprise choisie : {entreprise}',
  'rattach.demande.changer': 'Changer',
  'rattach.demande.message': 'Message (facultatif)',
  'rattach.demande.envoyer': 'Envoyer la demande',
  'rattach.demande.effet':
    'Une fois acceptée, votre fiche est masquée du public (conservée) et vos scans remontent chez elle.',
  'rattach.demande.enAttente': 'Demande envoyée à {entreprise}, en attente de validation.',
  'rattach.demande.annuler': 'Annuler la demande',
  'rattach.demande.refusee': 'Votre demande à {entreprise} n’a pas été acceptée.',
  'rattach.demande.motif': 'Motif : « {motif} »',
  'rattach.toast.envoyee': 'Demande envoyée.',
  'rattach.toast.annulee': 'Demande annulée.',
  'rattach.toast.erreur': 'L’action n’a pas abouti. Réessayez.',
  'rattach.erreur.link_request_pending': 'Une demande est déjà en cours pour votre compte.',
  'rattach.erreur.link_bad_status': 'Cette demande a déjà été traitée.',
  'rattach.erreur.recruiter_limit_reached': 'Le nombre maximal de recruteurs est atteint.',
  'rattach.erreur.link_target_invalid': 'Cette entreprise ne peut pas être choisie.',
  'rattach.erreur.link_requester_changed': 'La situation du demandeur a changé : la demande ne peut plus être acceptée.',

  // ── Récapitulatif « qui a fait quoi » ───────────────────────────────
  'rattach.recap.titre': 'Qui a fait quoi',
  'rattach.recap.total': 'Toute l’entreprise',
  'rattach.recap.vous': '{nom} (vous)',
  'rattach.recap.chargement': 'Calcul du récapitulatif.',
  'rattach.recap.erreur': 'Le récapitulatif n’a pas pu être calculé.',
  'rattach.recap.scans': '{n, plural, one {# scan} other {# scans}}',
  'rattach.recap.contacts': '{n, plural, one {# contact} other {# contacts}}',
  'rattach.recap.notes': '{n, plural, one {# note} other {# notes}}',
  'rattach.recap.etoiles': '{n, plural, one {# contact évalué} other {# contacts évalués}}',
  'rattach.recap.informations': '{n, plural, one {# échange qualifié} other {# échanges qualifiés}}',
  'rattach.recap.cv_recus': '{n, plural, one {# CV reçu} other {# CV reçus}}',
  'rattach.recap.cv_consultes': '{n, plural, one {# CV consulté} other {# CV consultés}}',
  'rattach.recap.entretiens': '{n, plural, one {# entretien} other {# entretiens}}',
  'rattach.recap.evaluations': '{n, plural, one {# évaluation d’entretien} other {# évaluations d’entretien}}',
  'rattach.recap.candidatures':
    '{n, plural, =0 {Aucune candidature adressée à l’entreprise} one {# candidature adressée à l’entreprise} other {# candidatures adressées à l’entreprise}}',
  'rattach.recap.aide':
    'Contacts et CV comptent des personnes : un visiteur vu par deux recruteurs ne compte qu’une fois dans le total.',
  'rattach.recap.rien': 'Rien pour l’instant.',

  // ── Ma fiche, section Stand ─────────────────────────────────────────
  'rattach.stand.ligne':
    '{n, plural, =0 {Aucun recruteur avec accès} one {# recruteur avec accès} other {# recruteurs avec accès}}',
  'rattach.stand.gerer': 'Gérer',

  // ── Organisation : onglet Rattachements ─────────────────────────────
  'rattachAdmin.onglet': 'Rattachements',
  'rattachAdmin.intro':
    'Un compte exposant peut demander à rejoindre une autre entreprise. À l’acceptation, sa fiche est masquée (conservée) et il devient recruteur de l’entreprise ; le détacher lui rend sa fiche.',
  'rattachAdmin.filtre': 'Statut',
  'rattachAdmin.filtre.tous': 'Toutes',
  'rattachAdmin.statut.en_attente': 'En attente',
  'rattachAdmin.statut.acceptee': 'Acceptée',
  'rattachAdmin.statut.refusee': 'Refusée',
  'rattachAdmin.statut.annulee': 'Annulée',
  'rattachAdmin.statut.detachee': 'Détachée',
  'rattachAdmin.col.demandeur': 'Demandeur',
  'rattachAdmin.col.cible': 'Entreprise demandée',
  'rattachAdmin.col.statut': 'Statut',
  'rattachAdmin.col.date': 'Demandée le',
  'rattachAdmin.col.actions': 'Actions',
  'rattachAdmin.origine': 'sa fiche : {nom}',
  'rattachAdmin.mere': 'compte principal : {nom}',
  'rattachAdmin.motif': 'Motif : {motif}',
  'rattachAdmin.vide': 'Aucune demande.',
  'rattachAdmin.chargement': 'Chargement des demandes.',
  'rattachAdmin.erreur': 'Les demandes n’ont pas pu être chargées.',
  'rattachAdmin.toast.accepte': 'Rattachement accepté.',
  'rattachAdmin.toast.refuse': 'Demande refusée.',
  'rattachAdmin.toast.detache': 'Compte détaché, sa fiche lui est rendue.',
  'rattachAdmin.toast.erreur': 'L’action n’a pas abouti : {raison}',
};
